import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  MessageCircle,
  HeadphonesIcon,
  CheckCircle,
  Loader2,
  AlertCircle,
  ExternalLink,
  Instagram,
  Facebook,
  Youtube,
  Twitter,
} from 'lucide-react';
import { fetchPublicContactPage, submitContactInquiry, type ContactPageData } from '../api/contactPage';
import { mapEmbedSrc } from '../utils/mapEmbed';

const SERVER = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';

function mediaUrl(path?: string) {
  if (!path?.trim()) return '';
  if (path.startsWith('http')) return path;
  return `${SERVER}${path.startsWith('/') ? path : `/${path}`}`;
}

const ContactUs: React.FC = () => {
  const [page, setPage] = useState<ContactPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '', hp: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ title: string; message: string } | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const loadPage = useCallback(async () => {
    try {
      const data = await fetchPublicContactPage();
      setPage(data);
      setLoadError(data ? null : 'Contact page is not available.');
    } catch {
      setLoadError('Failed to load contact page. Ensure the backend is running on port 5001.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!page?.form?.enabled) {
      setSubmitError('Contact form is currently unavailable. Please try again later.');
      return;
    }
    // Honeypot filled — treat as success (blocks bots; avoids silent no-op if autofill hits hidden field)
    if (formData.hp.trim()) {
      setSuccess({
        title: page.form.successTitle || 'Message Sent!',
        message: page.form.successMessage || 'Thank you for contacting us.',
      });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '', hp: '' });
      return;
    }

    const fieldsCfg = page.form.fields as Record<
      string,
      { enabled?: boolean; required?: boolean; label?: string }
    >;
    const name = formData.name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();
    const subject = formData.subject.trim();
    const msg = formData.message.trim();

    const showValidationError = (message: string) => {
      setSubmitError(message);
      setTimeout(() => {
        document.getElementById('contact-form-errors')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
    };

    if (fieldsCfg.name?.enabled !== false && fieldsCfg.name?.required && !name) {
      showValidationError('Please enter your name.');
      return;
    }
    if (fieldsCfg.email?.enabled !== false && fieldsCfg.email?.required && !email) {
      showValidationError('Please enter your email address.');
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showValidationError('Please enter a valid email address.');
      return;
    }
    if (fieldsCfg.phone?.enabled && fieldsCfg.phone?.required && !phone) {
      showValidationError('Please enter your phone number.');
      return;
    }
    if (fieldsCfg.subject?.enabled !== false && fieldsCfg.subject?.required && !subject) {
      showValidationError('Please select a subject from the list.');
      return;
    }
    if (msg.length < 10) {
      showValidationError('Please enter a message of at least 10 characters.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await submitContactInquiry({
        name,
        email,
        phone,
        subject,
        message: msg,
        website: formData.hp,
      });
      setSuccess({
        title: result.successTitle || page.form.successTitle || 'Message Sent!',
        message: result.successMessage || page.form.successMessage || 'Thank you for contacting us.',
      });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '', hp: '' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send message';
      setSubmitError(msg);
      setTimeout(() => {
        document.getElementById('contact-form-errors')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
    } finally {
      setSubmitting(false);
    }
  };

  const derived = useMemo(() => {
    if (!page) return null;
    const phones = (page.contactInfo.phones || []).filter((p) => p.enabled && p.value);
    const emails = (page.contactInfo.emails || []).filter((e) => e.enabled && e.value);
    const hours = (page.contactInfo.businessHours || []).filter((h) => h.enabled);
    const faqItems = (page.faq.items || []).filter((f) => f.enabled && f.question);
    const socials = Object.entries(page.socialLinks || {}).filter(([, v]) => v?.enabled && v?.url);
    const embedSrc = mapEmbedSrc(page.map);
    const fields = page.form.fields as Record<
      string,
      { enabled?: boolean; required?: boolean; label?: string; placeholder?: string; options?: { value: string; label: string }[] }
    >;

    const apiCards = (page.contactCards || [])
      .filter((c) => c.enabled && c.value)
      .sort((a, b) => a.order - b.order);

    type CardIcon = 'phone' | 'mail' | 'map';
    const fallbackCards: { icon: CardIcon; title: string; value: string; subtitle: string }[] = [
      ...phones.map((p) => ({
        icon: 'phone' as CardIcon,
        title: p.label || 'Phone',
        value: p.value,
        subtitle: 'Call us',
      })),
      ...emails.map((e) => ({
        icon: 'mail' as CardIcon,
        title: e.label || 'Email',
        value: e.value,
        subtitle: 'Email support',
      })),
    ];
    if (page.contactInfo.officeAddress) {
      fallbackCards.push({
        icon: 'map' as CardIcon,
        title: 'Visit us',
        value: page.contactInfo.officeAddress,
        subtitle: page.contactInfo.addressLine2 || 'Our office',
      });
    }

    const contactCards =
      apiCards.length > 0
        ? apiCards.map((c) => ({
            icon: (c.icon || 'phone') as CardIcon,
            title: c.title,
            value: c.value,
            subtitle: c.subtitle,
          }))
        : fallbackCards;

    return { phones, emails, hours, faqItems, socials, embedSrc, fields, contactCards };
  }, [page]);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-indigo-600" aria-label="Loading" />
      </div>
    );
  }

  if (!page || loadError || !derived) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4">
        <p className="text-center text-slate-600">{loadError || 'Contact page unavailable.'}</p>
      </div>
    );
  }

  const { hours, faqItems, socials, embedSrc, fields, contactCards } = derived;
  const subjectField = fields.subject;
  const hasHoursBlock = hours.length > 0 || page.page.supportText || page.contactInfo.onlineSupportNote;

  return (
    <div className="w-full overflow-x-hidden bg-slate-50">
      {/* Hero — top padding clears sticky header (h-14 mobile / h-16+nav desktop) */}
      <section
        className="relative isolate overflow-hidden text-white"
        style={
          page.page.bannerImageUrl
            ? {
                backgroundImage: `linear-gradient(rgba(15,23,42,${page.page.bannerOverlayOpacity ?? 0.55}), rgba(30,27,75,${page.page.bannerOverlayOpacity ?? 0.55})), url(${mediaUrl(page.page.bannerImageUrl)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : undefined
        }
      >
        <div
          className={`px-4 pb-12 pt-20 sm:px-6 sm:pb-14 sm:pt-24 lg:pb-16 ${
            !page.page.bannerImageUrl ? 'bg-gradient-to-br from-indigo-600 via-indigo-700 to-violet-800' : ''
          }`}
        >
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">{page.page.title}</h1>
            <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-indigo-100 sm:text-lg">
              {page.page.subtitle}
            </p>
            {socials.length > 0 && (
              <div className="mt-6 flex flex-wrap justify-center gap-2 sm:gap-3">
                {socials.map(([key, link]) => (
                  <a
                    key={key}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-full bg-white/15 p-2.5 backdrop-blur transition hover:bg-white/25"
                    aria-label={key}
                  >
                    <SocialIcon name={key} />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Page body — extra bottom padding on mobile so Send button clears fixed bottom nav */}
      <div className="mx-auto w-full max-w-6xl space-y-10 px-4 py-10 pb-32 sm:px-6 sm:py-12 sm:pb-12 lg:space-y-14 lg:pb-14">
        {/* Contact cards */}
        {contactCards.length > 0 && (
          <section aria-label="Contact methods">
            <div
              className={`grid gap-4 sm:gap-5 ${
                contactCards.length === 1
                  ? 'max-w-md mx-auto'
                  : contactCards.length === 2
                    ? 'sm:grid-cols-2'
                    : 'sm:grid-cols-2 lg:grid-cols-3'
              }`}
            >
              {contactCards.map((card, i) => (
                <ContactCard
                  key={`${card.title}-${i}`}
                  icon={cardIcon(card.icon)}
                  title={card.title}
                  value={card.value}
                  subtitle={card.subtitle}
                  href={cardHref(card.icon, card.value)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Hours + support */}
        {hasHoursBlock && (
          <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm sm:p-8">
            <div className="mb-6 flex items-center gap-3">
              <Clock className="h-6 w-6 shrink-0 text-indigo-600" />
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">Working hours</h2>
            </div>
            <div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
              {hours.length > 0 && (
                <div className="min-w-0 space-y-1">
                  {hours.map((h) => (
                    <div
                      key={h.day}
                      className="flex flex-col gap-0.5 border-b border-slate-100 py-2.5 text-sm last:border-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                    >
                      <span className="shrink-0 text-slate-600">{h.day}</span>
                      <span className="font-semibold text-slate-900 sm:text-right">{h.hours}</span>
                    </div>
                  ))}
                </div>
              )}
              {(page.page.supportHeading || page.page.supportText || page.contactInfo.onlineSupportNote) && (
                <div className="min-w-0 rounded-xl bg-indigo-50 p-4 sm:p-5">
                  <div className="mb-2 flex items-center gap-2 font-semibold text-indigo-900">
                    <HeadphonesIcon className="h-5 w-5 shrink-0" />
                    <span>{page.page.supportHeading || 'Support'}</span>
                  </div>
                  {page.page.supportText && (
                    <p className="text-sm leading-relaxed text-indigo-800">{page.page.supportText}</p>
                  )}
                  {page.contactInfo.onlineSupportNote && (
                    <p className="mt-2 text-xs leading-relaxed text-indigo-700">{page.contactInfo.onlineSupportNote}</p>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Form */}
        {page.form.enabled !== false && (
          <section className="rounded-2xl border border-slate-200/80 bg-white p-5 pb-28 shadow-sm sm:p-8 sm:pb-10 lg:p-10">
            <div className="mb-8 text-center">
              <MessageCircle className="mx-auto mb-3 h-11 w-11 text-indigo-600" />
              <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">{page.form.heading}</h2>
              {page.form.description && (
                <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">
                  {page.form.description}
                </p>
              )}
            </div>

            {success ? (
              <div className="py-8 text-center">
                <CheckCircle className="mx-auto mb-4 h-14 w-14 text-emerald-500" />
                <h3 className="text-xl font-bold text-slate-900 sm:text-2xl">{success.title}</h3>
                <p className="mx-auto mt-2 max-w-md text-slate-600">{success.message}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mx-auto max-w-2xl space-y-5" noValidate>
                {/* Honeypot — hidden from users, blocks bots */}
                <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden>
                  <label htmlFor="contact-hp">Leave blank</label>
                  <input
                    id="contact-hp"
                    type="text"
                    name="hp_field"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.hp}
                    onChange={(e) => setFormData({ ...formData, hp: e.target.value })}
                  />
                </div>
                {submitError && (
                  <div id="contact-form-errors" className="flex items-start gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{submitError}</span>
                  </div>
                )}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {fields.name?.enabled !== false && (
                    <FormField
                      label={fields.name.label || 'Name'}
                      required={fields.name.required}
                      value={formData.name}
                      onChange={(v) => setFormData({ ...formData, name: v })}
                      placeholder={fields.name.placeholder}
                    />
                  )}
                  {fields.email?.enabled !== false && (
                    <FormField
                      label={fields.email.label || 'Email'}
                      type="email"
                      required={fields.email.required}
                      value={formData.email}
                      onChange={(v) => setFormData({ ...formData, email: v })}
                      placeholder={fields.email.placeholder}
                    />
                  )}
                </div>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {fields.phone?.enabled && (
                    <FormField
                      label={fields.phone.label || 'Phone'}
                      type="tel"
                      required={fields.phone.required}
                      value={formData.phone}
                      onChange={(v) => setFormData({ ...formData, phone: v })}
                      placeholder={fields.phone.placeholder}
                    />
                  )}
                  {subjectField?.enabled !== false &&
                    ((subjectField.options || []).length > 0 ? (
                      <label className="block min-w-0 text-sm">
                        <span className="mb-1.5 block font-medium text-slate-700">
                          {subjectField.label || 'Subject'}
                          {subjectField.required ? ' *' : ''}
                        </span>
                        <select
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                        >
                          <option value="">Select a subject</option>
                          {(subjectField.options || []).map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : (
                      <FormField
                        label={subjectField.label || 'Subject'}
                        required={subjectField.required}
                        value={formData.subject}
                        onChange={(v) => setFormData({ ...formData, subject: v })}
                        placeholder="What is this about?"
                      />
                    ))}
                </div>
                {fields.message?.enabled !== false && (
                  <label className="block text-sm">
                    <span className="mb-1.5 block font-medium text-slate-700">
                      {fields.message.label || 'Message'}
                      {fields.message.required ? ' *' : ''}
                    </span>
                    <textarea
                      required={fields.message.required}
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder={fields.message.placeholder}
                      className="w-full resize-y rounded-xl border border-slate-200 px-4 py-3 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                    />
                  </label>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="relative z-10 flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3.5 font-semibold text-white shadow-md transition hover:bg-indigo-700 disabled:opacity-60 sm:py-4"
                >
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  {submitting ? 'Sending…' : page.form.submitButtonText || 'Send Message'}
                </button>
              </form>
            )}
          </section>
        )}

        {/* FAQ */}
        {page.faq.enabled !== false && faqItems.length > 0 && (
          <section>
            <h2 className="mb-5 text-center text-xl font-bold text-slate-900 sm:text-2xl">{page.faq.heading}</h2>
            <div className="mx-auto max-w-3xl space-y-3">
              {faqItems.map((item, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div key={idx} className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      className="flex w-full items-start gap-3 px-4 py-4 text-left sm:px-5"
                      aria-expanded={isOpen}
                    >
                      <span className="min-w-0 flex-1 font-medium leading-snug text-slate-900">{item.question}</span>
                      <span className="mt-0.5 shrink-0 text-lg font-medium text-indigo-600" aria-hidden>
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="border-t border-slate-100 px-4 pb-4 pt-3 text-sm leading-relaxed text-slate-600 sm:px-5">
                        {item.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Custom sections */}
        {(page.customSections || [])
          .filter((s) => s.enabled && s.title)
          .sort((a, b) => a.order - b.order)
          .map((section) => (
            <section
              key={section.id}
              className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm sm:p-6"
            >
              <h2 className="mb-3 text-lg font-bold text-slate-900 sm:text-xl">{section.title}</h2>
              <div className="text-sm leading-relaxed text-slate-600 whitespace-pre-wrap break-words">
                {section.content}
              </div>
            </section>
          ))}

        {/* Map */}
        {page.map.enabled !== false && (
          <section className="pb-2">
            <h2 className="mb-5 text-center text-xl font-bold text-slate-900 sm:text-2xl">
              {page.map.heading || 'Find Us'}
            </h2>
            <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
              <div className="relative aspect-[16/10] w-full max-h-[28rem] min-h-[220px] sm:aspect-[16/9]">
                {embedSrc ? (
                  <iframe
                    title="Store location"
                    src={embedSrc}
                    className="absolute inset-0 h-full w-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-100 px-4 text-center text-slate-600">
                    <MapPin className="mb-2 h-10 w-10 shrink-0 text-slate-400" />
                    <p className="max-w-md break-words text-sm font-medium">
                      {page.map.locationLabel || page.contactInfo.officeAddress || 'Location not configured'}
                    </p>
                  </div>
                )}
              </div>
              {page.map.openInMapsUrl && (
                <div className="border-t border-slate-100 p-4 text-center">
                  <a
                    href={page.map.openInMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
                  >
                    Open in Maps
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

function cardIcon(icon: string) {
  switch (icon) {
    case 'mail':
      return <Mail className="h-7 w-7 text-violet-600" />;
    case 'map':
      return <MapPin className="h-7 w-7 text-emerald-600" />;
    default:
      return <Phone className="h-7 w-7 text-indigo-600" />;
  }
}

function cardHref(icon: string, value: string) {
  if (!value) return undefined;
  if (icon === 'mail') return `mailto:${value}`;
  if (icon === 'phone') return `tel:${value.replace(/\s/g, '')}`;
  return undefined;
}

function ContactCard({
  icon,
  title,
  value,
  subtitle,
  href,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  subtitle: string;
  href?: string;
}) {
  const inner = (
    <>
      <div className="mx-auto mb-4 flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-slate-50">
        {icon}
      </div>
      <h3 className="text-base font-bold text-slate-900 sm:text-lg">{title}</h3>
      <p className="mt-2 break-words text-sm leading-relaxed text-slate-600">{value}</p>
      {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
    </>
  );

  const className =
    'flex h-full min-w-0 flex-col rounded-2xl border border-slate-200/80 bg-white p-5 text-center shadow-sm transition hover:shadow-md sm:p-6';

  if (href) {
    return (
      <a href={href} className={`${className} hover:border-indigo-200`}>
        {inner}
      </a>
    );
  }

  return <div className={className}>{inner}</div>;
}

function FormField({
  label,
  value,
  onChange,
  type = 'text',
  required,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="block min-w-0 text-sm">
      <span className="mb-1.5 block font-medium text-slate-700">
        {label}
        {required ? ' *' : ''}
      </span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full min-w-0 rounded-xl border border-slate-200 px-4 py-3 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
      />
    </label>
  );
}

function SocialIcon({ name }: { name: string }) {
  const cls = 'h-5 w-5';
  switch (name) {
    case 'instagram':
      return <Instagram className={cls} />;
    case 'facebook':
      return <Facebook className={cls} />;
    case 'youtube':
      return <Youtube className={cls} />;
    case 'twitter':
      return <Twitter className={cls} />;
    default:
      return <MessageCircle className={cls} />;
  }
}

export default ContactUs;
